import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import {
  X, FileText, Plus, Trash2, Type, PenTool, Calendar, CheckSquare,
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, MousePointer, Move
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import uploadService from '../../../services/uploadService';
import * as pdfjsLib from 'pdfjs-dist';

// Set PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const FIELD_TYPES = [
  { id: 'text', name: 'Text', icon: Type, color: '#6a1fbf' },
  { id: 'signature', name: 'Signature', icon: PenTool, color: '#00c853' },
  { id: 'date', name: 'Date', icon: Calendar, color: '#ff9800' },
  { id: 'initials', name: 'Initials', icon: Type, color: '#2196f3' },
  { id: 'checkbox', name: 'Checkbox', icon: CheckSquare, color: '#e91e63' },
];

const FIELD_ROLES = [
  { id: 'participant', name: 'Participant fills' },
  { id: 'host', name: 'Host fills' },
];

const DEFAULT_FIELD_SIZE = {
  text: { width: 20, height: 3 },
  signature: { width: 25, height: 8 },
  date: { width: 15, height: 3 },
  initials: { width: 10, height: 5 },
  checkbox: { width: 3, height: 3 },
};

const FONT_SIZE_OPTIONS = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32];

const COLOR_PRESETS = [
  '#000000', '#333333', '#666666',
  '#6a1fbf', '#2196f3', '#00c853',
  '#ff9800', '#e91e63', '#f44336',
];

export default function ContractModal({
  isOpen,
  onClose,
  onSave,
  contract = null,
  folders = [],
  currentFolder = null,
  skipFolderSelect = false,
  readOnly = false,
  onCopyToMyElements,
}) {
  const [formData, setFormData] = useState({
    name: '',
    folder_id: currentFolder?.id || '',
    display_mode: 'fullscreen',
    mode: 'interactive',
    file_url: '',
    file_name: '',
    file_type: '',
    editable_fields: [],
    host_fields: {},
    content: '',
    integration: { provider: 'none', sync_enabled: false },
  });
  const [uploading, setUploading] = useState(false);
  const [activeStep, setActiveStep] = useState('upload'); // upload | place
  const [pdfPages, setPdfPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pdfScale, setPdfScale] = useState(1);
  const [placingFieldType, setPlacingFieldType] = useState(null);
  const [placingFieldRole, setPlacingFieldRole] = useState('participant');
  const [selectedField, setSelectedField] = useState(null);
  const [draggingField, setDraggingField] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizingField, setResizingField] = useState(null); // { fieldId, handle, startX, startY, startW, startH }
  const wasDraggingRef = useRef(false);
  const canvasContainerRef = useRef(null);
  const pdfDocRef = useRef(null);

  // Folder select dropdown
  const [folderDropdownOpen, setFolderDropdownOpen] = useState(false);

  useEffect(() => {
    if (contract) {
      setFormData({
        name: contract.name || '',
        folder_id: contract.folder_id || currentFolder?.id || '',
        display_mode: contract.display_mode || 'fullscreen',
        mode: contract.mode || 'interactive',
        file_url: contract.file_url || '',
        file_name: contract.file_name || '',
        file_type: contract.file_type || '',
        editable_fields: contract.editable_fields || [],
        host_fields: contract.host_fields || {},
        content: contract.content || '',
        integration: contract.integration || { provider: 'none', sync_enabled: false },
      });
      if (contract.file_url && contract.editable_fields?.length > 0) {
        setActiveStep('place');
      } else if (contract.file_url) {
        setActiveStep('place');
      } else {
        setActiveStep('upload');
      }
    } else {
      setFormData({
        name: '',
        folder_id: currentFolder?.id || '',
        display_mode: 'fullscreen',
        mode: 'interactive',
        file_url: '',
        file_name: '',
        file_type: '',
        editable_fields: [],
        host_fields: {},
        content: '',
        integration: { provider: 'none', sync_enabled: false },
      });
      setActiveStep('upload');
    }
    setPdfPages([]);
    setCurrentPage(1);
    setTotalPages(0);
    setSelectedField(null);
    setPlacingFieldType(null);
  }, [contract, isOpen, currentFolder?.id]);

  // Load document pages when file_url changes
  useEffect(() => {
    if (!formData.file_url) return;
    if (formData.file_type === 'pdf') {
      loadPdf(formData.file_url);
    } else if (formData.file_type === 'image') {
      loadImage(formData.file_url);
    }
    // eslint-disable-next-line
  }, [formData.file_url, formData.file_type]);

  const loadPdf = async (url) => {
    try {
      // Proxy S3 URLs through backend to add CORS headers (S3 bucket
      // doesn't send them, and pdfjs requires same-origin or CORS).
      const proxyBase = process.env.REACT_APP_BASE_URL || '';
      const proxiedUrl = proxyBase
        ? `${proxyBase.replace(/\/$/, '')}/proxy/resource?url=${encodeURIComponent(url)}`
        : url;
      const loadingTask = pdfjsLib.getDocument(proxiedUrl);
      const pdf = await loadingTask.promise;
      pdfDocRef.current = pdf;
      setTotalPages(pdf.numPages);
      setCurrentPage(1);

      // Render all pages
      const pages = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        await page.render({ canvasContext: context, viewport }).promise;
        pages.push({
          pageNum: i,
          dataUrl: canvas.toDataURL(),
          width: viewport.width,
          height: viewport.height,
        });
      }
      setPdfPages(pages);
    } catch (err) {
      console.error('Failed to load PDF:', err);
    }
  };

  const loadImage = (url) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Scale image to similar dimensions as PDF pages (scale to ~900px wide max)
      const maxWidth = 900;
      const scale = img.width > maxWidth ? maxWidth / img.width : 1;
      const w = img.width * scale;
      const h = img.height * scale;
      setPdfPages([{
        pageNum: 1,
        dataUrl: url,
        width: w,
        height: h,
      }]);
      setTotalPages(1);
      setCurrentPage(1);
    };
    img.onerror = () => {
      console.error('Failed to load image:', url);
      // Still set it as a page so we can show something
      setPdfPages([{
        pageNum: 1,
        dataUrl: url,
        width: 900,
        height: 1200,
      }]);
      setTotalPages(1);
      setCurrentPage(1);
    };
    img.src = url;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
    ];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PDF, DOCX, JPG, or PNG file');
      return;
    }

    let fileType = 'pdf';
    if (file.type.startsWith('image/')) fileType = 'image';
    else if (file.type.includes('wordprocessingml')) fileType = 'docx';

    setUploading(true);
    try {
      const result = await uploadService.uploadFile(file);

      if (fileType === 'docx') {
        // Convert DOCX to PDF on the backend
        try {
          const { default: requests } = await import('../../../services/httpServices');
          const convertResult = await requests.post('/contract/convert-docx', { fileUrl: result.url });
          if (convertResult.success && convertResult.pdfUrl) {
            setFormData(prev => ({
              ...prev,
              file_url: convertResult.pdfUrl,
              file_name: file.name.replace(/\.docx$/i, '.pdf'),
              file_type: 'pdf',
              original_file_url: result.url,
              original_file_name: file.name,
            }));
          } else {
            alert('Failed to convert DOCX to PDF');
          }
        } catch (convertErr) {
          console.error('DOCX conversion failed:', convertErr);
          alert('Failed to convert DOCX. Please upload a PDF instead.');
        }
      } else {
        setFormData(prev => ({
          ...prev,
          file_url: result.url,
          file_name: file.name,
          file_type: fileType,
        }));
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // Click on PDF page to place a field or deselect
  const handlePageClick = useCallback((e, pageNum) => {
    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      return;
    }
    if (!placingFieldType) {
      // Clicking empty area deselects the current field
      setSelectedField(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const size = DEFAULT_FIELD_SIZE[placingFieldType] || { width: 20, height: 3 };

    // Signature/initials fields default to 'host' role (host signs first, then sends to participants)
    const effectiveRole = (placingFieldType === 'signature' || placingFieldType === 'initials')
      ? (placingFieldRole || 'host')
      : placingFieldRole;

    const newField = {
      id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      type: placingFieldType,
      role: effectiveRole,
      label: placingFieldType === 'signature'
        ? `${effectiveRole === 'host' ? 'Host' : 'Participant'} Signature`
        : placingFieldType === 'initials'
          ? `${effectiveRole === 'host' ? 'Host' : 'Participant'} Initials`
          : placingFieldType === 'checkbox'
            ? 'I Agree'
            : `${placingFieldType.charAt(0).toUpperCase() + placingFieldType.slice(1)} Field`,
      page: pageNum,
      x: Math.max(0, Math.min(x - size.width / 2, 100 - size.width)),
      y: Math.max(0, Math.min(y - size.height / 2, 100 - size.height)),
      width: size.width,
      height: size.height,
      required: true,
      placeholder: '',
      value: placingFieldType === 'date' ? new Date().toISOString().split('T')[0] : '',
      fontSize: 12,
      fontColor: '#000000',
      autoFillDate: placingFieldType === 'date', // auto-fill with current date on participant side
    };

    setFormData(prev => ({
      ...prev,
      editable_fields: [...prev.editable_fields, newField],
    }));
    setSelectedField(newField.id);
    setPlacingFieldType(null); // Clear after placing — user must click toolbar again for next field
  }, [placingFieldType, placingFieldRole]);

  // Drag field on PDF
  const handleFieldMouseDown = (e, field) => {
    e.stopPropagation();
    setSelectedField(field.id);
    if (!placingFieldType) {
      setDraggingField(field.id);
      const fieldEl = e.currentTarget;
      const rect = fieldEl.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handlePageMouseMove = useCallback((e, pageNum) => {
    const rect = e.currentTarget.getBoundingClientRect();

    // Handle resize
    if (resizingField) {
      wasDraggingRef.current = true;
      const field = formData.editable_fields.find(f => f.id === resizingField.fieldId);
      if (!field || field.page !== pageNum) return;

      const dx = ((e.clientX - resizingField.startMouseX) / rect.width) * 100;
      const dy = ((e.clientY - resizingField.startMouseY) / rect.height) * 100;
      const handle = resizingField.handle;

      let newW = resizingField.startW;
      let newH = resizingField.startH;
      let newX = resizingField.startFieldX;
      let newY = resizingField.startFieldY;

      if (handle.includes('r')) newW = Math.max(2, resizingField.startW + dx);
      if (handle.includes('l')) { newW = Math.max(2, resizingField.startW - dx); newX = resizingField.startFieldX + dx; }
      if (handle.includes('b')) newH = Math.max(1.5, resizingField.startH + dy);
      if (handle.includes('t')) { newH = Math.max(1.5, resizingField.startH - dy); newY = resizingField.startFieldY + dy; }

      // Clamp
      newX = Math.max(0, Math.min(newX, 100 - newW));
      newY = Math.max(0, Math.min(newY, 100 - newH));

      setFormData(prev => ({
        ...prev,
        editable_fields: prev.editable_fields.map(f =>
          f.id === resizingField.fieldId
            ? { ...f, x: newX, y: newY, width: newW, height: newH }
            : f
        ),
      }));
      return;
    }

    // Handle drag
    if (!draggingField) return;
    wasDraggingRef.current = true;
    const field = formData.editable_fields.find(f => f.id === draggingField);
    if (!field || field.page !== pageNum) return;

    const newX = ((e.clientX - rect.left - dragOffset.x) / rect.width) * 100;
    const newY = ((e.clientY - rect.top - dragOffset.y) / rect.height) * 100;

    setFormData(prev => ({
      ...prev,
      editable_fields: prev.editable_fields.map(f =>
        f.id === draggingField
          ? { ...f, x: Math.max(0, Math.min(newX, 100 - f.width)), y: Math.max(0, Math.min(newY, 100 - f.height)) }
          : f
      ),
    }));
  }, [draggingField, dragOffset, resizingField, formData.editable_fields]);

  const handleMouseUp = useCallback(() => {
    setDraggingField(null);
    setResizingField(null);
    // Keep wasDraggingRef true so the subsequent click event is suppressed,
    // then reset it asynchronously after the click has fired
    if (wasDraggingRef.current) {
      setTimeout(() => { wasDraggingRef.current = false; }, 0);
    }
  }, []);

  useEffect(() => {
    if (draggingField || resizingField) {
      window.addEventListener('mouseup', handleMouseUp);
      return () => window.removeEventListener('mouseup', handleMouseUp);
    }
  }, [draggingField, resizingField, handleMouseUp]);

  const removeField = (fieldId) => {
    setFormData(prev => ({
      ...prev,
      editable_fields: prev.editable_fields.filter(f => f.id !== fieldId),
    }));
    if (selectedField === fieldId) setSelectedField(null);
  };

  const updateField = (fieldId, updates) => {
    setFormData(prev => ({
      ...prev,
      editable_fields: prev.editable_fields.map(f =>
        f.id === fieldId ? { ...f, ...updates } : f
      ),
    }));
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('Please enter a contract name');
      return;
    }
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  const currentPageData = pdfPages.find(p => p.pageNum === currentPage);
  const currentPageFields = formData.editable_fields.filter(f => f.page === currentPage);
  const selectedFieldData = formData.editable_fields.find(f => f.id === selectedField);

  // Color fields by role: host fields are orange, participant fields are blue
  const ROLE_COLORS = { host: '#ff9800', participant: '#2196f3' };
  const getFieldColor = (type, role) => {
    return ROLE_COLORS[role] || ROLE_COLORS.participant;
  };

  const modalContent = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      style={{ zIndex: 9999 }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-6xl bg-[#0A0F1E] rounded-xl border border-white/10 shadow-2xl flex flex-col"
        style={{ height: '90vh' }}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-purple-400" />
            <h2 className="text-lg font-semibold text-white">
              {contract ? 'Edit Contract' : 'New Contract'}
            </h2>
            {activeStep === 'place' && (
              <span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded">
                Click on document to place fields
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Step 1: Upload */}
        {activeStep === 'upload' && (
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-lg mx-auto space-y-6">
              {/* Contract Name */}
              <div>
                <label className="text-white text-sm mb-2 block">Contract Name *</label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  disabled={readOnly}
                  placeholder="e.g., White Label Reseller Agreement"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Folder */}
              {!skipFolderSelect && folders.length > 0 && (
                <div className="relative">
                  <label className="text-white text-sm mb-2 block">Folder</label>
                  <button
                    onClick={() => setFolderDropdownOpen(!folderDropdownOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:bg-white/10"
                  >
                    <span>{folders.find(f => f.id === formData.folder_id)?.name || 'Select folder...'}</span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${folderDropdownOpen ? 'rotate-90' : ''}`} />
                  </button>
                  {folderDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl z-10 max-h-40 overflow-y-auto">
                      {folders.map(f => (
                        <button
                          key={f.id}
                          onClick={() => { setFormData(prev => ({ ...prev, folder_id: f.id })); setFolderDropdownOpen(false); }}
                          className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/10"
                        >
                          {f.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Upload */}
              <div>
                <label className="text-white text-sm mb-2 block">Upload Contract Document (PDF, DOCX, JPG, PNG) *</label>
                {formData.file_url ? (
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-green-400" />
                        <span className="text-sm text-white">{formData.file_name}</span>
                      </div>
                      {!readOnly && (
                        <button
                          onClick={() => setFormData(prev => ({ ...prev, file_url: '', file_name: '', file_type: '' }))}
                          className="text-sm text-red-400 hover:text-red-300"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <input
                    type="file"
                    accept=".pdf,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    disabled={uploading || readOnly}
                    className="block w-full text-sm text-gray-400
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-semibold
                      file:bg-purple-500 file:text-white
                      hover:file:bg-purple-600
                      file:cursor-pointer cursor-pointer
                      bg-white/5 border border-white/10 rounded-lg p-2"
                  />
                )}
                {uploading && <p className="text-xs text-blue-400 mt-2">Uploading...</p>}
              </div>

              {!formData.name.trim() && formData.file_url && (
                <p className="text-xs text-red-400">Please enter a contract name above</p>
              )}
              {!formData.file_url && formData.name.trim() && (
                <p className="text-xs text-red-400">Please upload a document</p>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Place fields on PDF */}
        {activeStep === 'place' && (
          <div className="flex-1 flex overflow-hidden">
            {/* Left toolbar */}
            <div className="w-64 border-r border-white/10 flex flex-col flex-shrink-0 overflow-y-auto">
              {/* Field type selector */}
              <div className="p-3 border-b border-white/10">
                <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wider">Place Field</p>
                <div className="space-y-1">
                  {FIELD_TYPES.map(ft => {
                    const Icon = ft.icon;
                    const isActive = placingFieldType === ft.id;
                    return (
                      <button
                        key={ft.id}
                        onClick={() => setPlacingFieldType(isActive ? null : ft.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          isActive
                            ? 'text-white ring-2'
                            : 'text-gray-400 hover:bg-white/5 hover:text-white'
                        }`}
                        style={isActive ? { backgroundColor: ft.color + '20', ringColor: ft.color } : {}}
                      >
                        <Icon className="w-4 h-4" style={isActive ? { color: ft.color } : {}} />
                        {ft.name}
                      </button>
                    );
                  })}
                </div>
                {placingFieldType && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-gray-500">Assigned to:</p>
                    {FIELD_ROLES.map(role => (
                      <button
                        key={role.id}
                        onClick={() => setPlacingFieldRole(role.id)}
                        className={`w-full text-left px-3 py-1.5 rounded text-xs transition-colors ${
                          placingFieldRole === role.id
                            ? 'bg-purple-500/20 text-purple-300'
                            : 'text-gray-400 hover:bg-white/5'
                        }`}
                      >
                        {role.name}
                      </button>
                    ))}
                  </div>
                )}
                {!placingFieldType && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-gray-600">
                    <MousePointer className="w-3 h-3" />
                    <span>Select a type, then click on the PDF</span>
                  </div>
                )}
              </div>

              {/* Placed fields list */}
              <div className="p-3 flex-1 overflow-y-auto">
                <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wider">
                  Fields ({formData.editable_fields.length})
                </p>
                {formData.editable_fields.length === 0 ? (
                  <p className="text-xs text-gray-600 italic">No fields placed yet</p>
                ) : (
                  <div className="space-y-1">
                    {formData.editable_fields.map(field => (
                      <div
                        key={field.id}
                        onClick={() => {
                          setSelectedField(field.id);
                          setCurrentPage(field.page);
                        }}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer transition-colors ${
                          selectedField === field.id
                            ? 'bg-white/10 text-white'
                            : 'text-gray-400 hover:bg-white/5'
                        }`}
                      >
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: getFieldColor(field.type, field.role) }}
                        />
                        <span className="truncate flex-1">{field.label}</span>
                        <span className="text-gray-600">p{field.page}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
                          className="p-0.5 text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected field properties */}
              {selectedFieldData && (
                <div className="p-3 border-t border-white/10 space-y-2 overflow-y-auto" style={{ maxHeight: '50vh' }}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Field Properties</p>
                    <button
                      onClick={() => setSelectedField(null)}
                      className="p-0.5 text-gray-500 hover:text-white hover:bg-white/10 rounded transition-colors"
                      title="Close properties"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Label</label>
                    <input
                      value={selectedFieldData.label}
                      onChange={(e) => updateField(selectedFieldData.id, { label: e.target.value })}
                      className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Assigned to</label>
                    <select
                      value={selectedFieldData.role || 'participant'}
                      onChange={(e) => updateField(selectedFieldData.id, { role: e.target.value })}
                      className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs focus:outline-none appearance-none"
                    >
                      <option value="participant" className="bg-[#1a1a2e]">Participant fills</option>
                      <option value="host" className="bg-[#1a1a2e]">Host fills</option>
                    </select>
                  </div>
                  {(selectedFieldData.type === 'text' || selectedFieldData.type === 'initials') && (
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Placeholder</label>
                      <input
                        value={selectedFieldData.placeholder || ''}
                        onChange={(e) => updateField(selectedFieldData.id, { placeholder: e.target.value })}
                        className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs focus:outline-none"
                        placeholder="Placeholder text..."
                      />
                    </div>
                  )}
                  {selectedFieldData.role === 'host' && (selectedFieldData.type === 'text' || selectedFieldData.type === 'date') && (
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Host Value (pre-fill)</label>
                      <input
                        type={selectedFieldData.type === 'date' ? 'date' : 'text'}
                        value={selectedFieldData.value || ''}
                        onChange={(e) => updateField(selectedFieldData.id, { value: e.target.value })}
                        className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs focus:outline-none"
                        placeholder="Enter host value..."
                      />
                    </div>
                  )}

                  {/* Auto-fill date toggle */}
                  {selectedFieldData.type === 'date' && (
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-gray-400">Auto-fill today's date</label>
                      <button
                        onClick={() => updateField(selectedFieldData.id, { autoFillDate: !selectedFieldData.autoFillDate })}
                        className={`w-8 h-4 rounded-full transition-colors ${selectedFieldData.autoFillDate ? 'bg-purple-500' : 'bg-white/20'}`}
                      >
                        <span className={`block w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${selectedFieldData.autoFillDate ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  )}

                  {/* Font Size */}
                  {selectedFieldData.type !== 'checkbox' && selectedFieldData.type !== 'signature' && (
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Font Size</label>
                      <div className="flex items-center gap-1">
                        <select
                          value={selectedFieldData.fontSize || 12}
                          onChange={(e) => updateField(selectedFieldData.id, { fontSize: Number(e.target.value) })}
                          className="flex-1 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs focus:outline-none appearance-none"
                        >
                          {FONT_SIZE_OPTIONS.map(s => (
                            <option key={s} value={s} className="bg-[#1a1a2e]">{s}px</option>
                          ))}
                        </select>
                        <button
                          onClick={() => updateField(selectedFieldData.id, { fontSize: Math.max(6, (selectedFieldData.fontSize || 12) - 1) })}
                          className="px-1.5 py-1 bg-white/5 border border-white/10 rounded text-white text-xs hover:bg-white/10"
                        >-</button>
                        <button
                          onClick={() => updateField(selectedFieldData.id, { fontSize: Math.min(48, (selectedFieldData.fontSize || 12) + 1) })}
                          className="px-1.5 py-1 bg-white/5 border border-white/10 rounded text-white text-xs hover:bg-white/10"
                        >+</button>
                      </div>
                    </div>
                  )}

                  {/* Font Color */}
                  {selectedFieldData.type !== 'checkbox' && selectedFieldData.type !== 'signature' && (
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Font Color</label>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {COLOR_PRESETS.map(c => (
                          <button
                            key={c}
                            onClick={() => updateField(selectedFieldData.id, { fontColor: c })}
                            className={`w-5 h-5 rounded border-2 transition-all ${
                              (selectedFieldData.fontColor || '#000000') === c ? 'border-white scale-110' : 'border-transparent hover:border-white/40'
                            }`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                        <input
                          type="color"
                          value={selectedFieldData.fontColor || '#000000'}
                          onChange={(e) => updateField(selectedFieldData.id, { fontColor: e.target.value })}
                          className="w-5 h-5 rounded cursor-pointer bg-transparent border-0 p-0"
                          title="Custom color"
                        />
                      </div>
                    </div>
                  )}

                  {/* Field Size */}
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Field Size</label>
                    <div className="grid grid-cols-2 gap-1">
                      <div>
                        <label className="text-[10px] text-gray-600">Width %</label>
                        <input
                          type="number"
                          min="2"
                          max="100"
                          step="0.5"
                          value={Math.round(selectedFieldData.width * 10) / 10}
                          onChange={(e) => updateField(selectedFieldData.id, { width: Math.max(2, Math.min(100, Number(e.target.value))) })}
                          className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-600">Height %</label>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          step="0.5"
                          value={Math.round(selectedFieldData.height * 10) / 10}
                          onChange={(e) => updateField(selectedFieldData.id, { height: Math.max(1, Math.min(100, Number(e.target.value))) })}
                          className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-xs text-gray-400">Required</label>
                    <button
                      onClick={() => updateField(selectedFieldData.id, { required: !selectedFieldData.required })}
                      className={`w-8 h-4 rounded-full transition-colors ${selectedFieldData.required ? 'bg-purple-500' : 'bg-white/20'}`}
                    >
                      <span className={`block w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${selectedFieldData.required ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <button
                    onClick={() => removeField(selectedFieldData.id)}
                    className="w-full px-2 py-1 text-xs text-red-400 border border-red-400/20 rounded hover:bg-red-500/10 transition-colors"
                  >
                    Delete Field
                  </button>
                </div>
              )}
            </div>

            {/* PDF viewer with overlay fields */}
            <div className="flex-1 flex flex-col overflow-hidden bg-[#111827]">
              {/* Page controls */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-[#0A0F1E] flex-shrink-0">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    className="p-1 text-gray-400 hover:text-white disabled:opacity-30"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-400">
                    Page {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    className="p-1 text-gray-400 hover:text-white disabled:opacity-30"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPdfScale(s => Math.max(0.5, s - 0.1))} className="p-1 text-gray-400 hover:text-white">
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-400">{Math.round(pdfScale * 100)}%</span>
                  <button onClick={() => setPdfScale(s => Math.min(2, s + 0.1))} className="p-1 text-gray-400 hover:text-white">
                    <ZoomIn className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  {placingFieldType && (
                    <span className="text-xs px-2 py-1 rounded-full text-white" style={{ backgroundColor: getFieldColor(placingFieldType, placingFieldRole) + '40' }}>
                      Placing: {FIELD_TYPES.find(f => f.id === placingFieldType)?.name}
                    </span>
                  )}
                  <button
                    onClick={() => { setPlacingFieldType(null); setSelectedField(null); }}
                    className={`p-1.5 rounded text-xs ${!placingFieldType ? 'bg-purple-500/20 text-purple-300' : 'text-gray-400 hover:bg-white/5'}`}
                    title="Select/Move mode"
                  >
                    <Move className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* PDF page with fields */}
              <div
                ref={canvasContainerRef}
                className="flex-1 overflow-auto flex items-start justify-center p-4"
              >
                {currentPageData ? (
                  <div
                    className="relative shadow-2xl"
                    style={{
                      width: currentPageData.width * pdfScale,
                      height: currentPageData.height * pdfScale,
                      cursor: placingFieldType ? 'crosshair' : 'default',
                    }}
                    onClick={(e) => handlePageClick(e, currentPage)}
                    onMouseMove={(e) => handlePageMouseMove(e, currentPage)}
                  >
                    <img
                      src={currentPageData.dataUrl}
                      alt={`Page ${currentPage}`}
                      className="w-full h-full pointer-events-none select-none"
                      draggable={false}
                    />
                    {/* Field overlays */}
                    {currentPageFields.map(field => {
                      const color = getFieldColor(field.type, field.role);
                      const isSelected = selectedField === field.id;
                      const isHostField = field.role === 'host';
                      const handleResize = (e, handle) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setSelectedField(field.id);
                        setResizingField({
                          fieldId: field.id,
                          handle,
                          startMouseX: e.clientX,
                          startMouseY: e.clientY,
                          startW: field.width,
                          startH: field.height,
                          startFieldX: field.x,
                          startFieldY: field.y,
                        });
                      };
                      return (
                        <div
                          key={field.id}
                          className={`absolute border-2 rounded cursor-move flex items-center justify-center transition-shadow ${
                            isSelected ? 'shadow-lg ring-2 ring-white/50' : ''
                          }`}
                          style={{
                            left: `${field.x}%`,
                            top: `${field.y}%`,
                            width: `${field.width}%`,
                            height: `${field.height}%`,
                            borderColor: color,
                            backgroundColor: color + '30',
                            zIndex: isSelected ? 20 : 10,
                          }}
                          onMouseDown={(e) => handleFieldMouseDown(e, field)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span
                            className="text-center leading-tight pointer-events-none select-none px-1 truncate"
                            style={{
                              fontSize: `${field.fontSize || 12}px`,
                              color: field.fontColor || '#ffffff',
                            }}
                          >
                            {field.placeholder || ''}
                            {isHostField && <span className="block text-yellow-300 opacity-75" style={{ fontSize: '7px' }}>HOST</span>}
                          </span>
                          {/* Resize handles — visible when selected */}
                          {isSelected && (
                            <>
                              {/* Corner handles */}
                              <div onMouseDown={(e) => handleResize(e, 'tl')} className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-white border border-gray-400 rounded-sm cursor-nw-resize" style={{ zIndex: 30 }} />
                              <div onMouseDown={(e) => handleResize(e, 'tr')} className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-white border border-gray-400 rounded-sm cursor-ne-resize" style={{ zIndex: 30 }} />
                              <div onMouseDown={(e) => handleResize(e, 'bl')} className="absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-white border border-gray-400 rounded-sm cursor-sw-resize" style={{ zIndex: 30 }} />
                              <div onMouseDown={(e) => handleResize(e, 'br')} className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-white border border-gray-400 rounded-sm cursor-se-resize" style={{ zIndex: 30 }} />
                              {/* Edge handles */}
                              <div onMouseDown={(e) => handleResize(e, 'r')} className="absolute top-1/2 -right-1 w-2 h-4 -translate-y-1/2 bg-white border border-gray-400 rounded-sm cursor-e-resize" style={{ zIndex: 30 }} />
                              <div onMouseDown={(e) => handleResize(e, 'l')} className="absolute top-1/2 -left-1 w-2 h-4 -translate-y-1/2 bg-white border border-gray-400 rounded-sm cursor-w-resize" style={{ zIndex: 30 }} />
                              <div onMouseDown={(e) => handleResize(e, 'b')} className="absolute -bottom-1 left-1/2 w-4 h-2 -translate-x-1/2 bg-white border border-gray-400 rounded-sm cursor-s-resize" style={{ zIndex: 30 }} />
                              <div onMouseDown={(e) => handleResize(e, 't')} className="absolute -top-1 left-1/2 w-4 h-2 -translate-x-1/2 bg-white border border-gray-400 rounded-sm cursor-n-resize" style={{ zIndex: 30 }} />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500">Loading document...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex items-center justify-between flex-shrink-0">
          <div>
            {activeStep === 'place' && (
              <button
                onClick={() => setActiveStep('upload')}
                className="px-4 py-2 border border-white/10 text-gray-300 rounded-lg hover:bg-white/10 text-sm"
              >
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 border border-white/10 text-gray-300 rounded-lg hover:bg-white/10 text-sm">
              Cancel
            </button>
            {readOnly ? (onCopyToMyElements && (
              <button
                onClick={onCopyToMyElements}
                className="px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90"
              >
                Copy to My Elements
              </button>
            )) : activeStep === 'upload' ? (
              <button
                onClick={() => {
                  if (!formData.name.trim()) {
                    toast.error('Please enter a contract name');
                    return;
                  }
                  if (!formData.file_url) {
                    toast.error('Please upload a document');
                    return;
                  }
                  setActiveStep('place');
                }}
                disabled={!formData.name.trim() || !formData.file_url}
                className="px-4 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg font-medium hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next: Place Fields
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={!formData.name.trim() || !formData.file_url}
                className="px-4 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg font-medium hover:opacity-90 transition-all disabled:opacity-40"
              >
                {contract ? 'Save Changes' : 'Create Contract'}
                {formData.editable_fields.length > 0 && ` (${formData.editable_fields.length} fields)`}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
